import React from "react";
import { 
  Activity, Hammer, ShoppingBag, Utensils, Factory, Building2, Laptop, Car, 
  ArrowRight, Search, CheckCircle, ShieldCheck, HelpCircle, MapPin, Sparkles, Building
} from "lucide-react";
import { getIndustryBySlug, getQualifiedIndustryLocation, IndustryRecord } from "../../utils/industryRegistry";
import { SupportedLocation } from "../../utils/locationRegistry";
import { getCategorySlugFromText, getCategoryBaseName, sanitizeDuplicateWords } from "../../utils/jobCategoryRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";
import { 
  SeoSearchActionModule, 
  SeoWhyPageExistsSection, 
  SeoExampleResultsPreview, 
  SeoTrustSignalBlock, 
  SeoCtaFooterBanner,
  SampleBusinessCard 
} from "./SeoUniversalPageEngine";

interface IndustryPageProps {
  industrySlug: string;
  citySlug?: string;
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function IndustryPage({ industrySlug, citySlug, onNavigate, onLogin = () => {}, currentUser }: IndustryPageProps) {
  const match = citySlug ? getQualifiedIndustryLocation(industrySlug, citySlug) : null;
  const industry: IndustryRecord | null = match ? match.industry : getIndustryBySlug(industrySlug);
  const location: SupportedLocation | null = match ? match.location : null;

  if (!industry) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-bold text-slate-800 mb-2">Industry Page Not Found</h1>
        <p className="text-sm text-slate-600 mb-4">The requested industry directory could not be located.</p>
        <button
          onClick={() => onNavigate("/industries")}
          className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-medium text-xs hover:bg-indigo-700 transition"
        >
          View Industry Directory
        </button>
      </div>
    );
  }

  const cityState = location ? `${location.cityName}, ${location.stateAbbreviation}` : "";
  const baseIndName = getCategoryBaseName(industry.name);
  const pageTitle = location
    ? sanitizeDuplicateWords(`${baseIndName} Jobs & Employers in ${cityState} | BizNearYou`)
    : sanitizeDuplicateWords(`${baseIndName} - Local Employers & Career Pathways | BizNearYou`);

  const metaDescription = location
    ? sanitizeDuplicateWords(`Find independent ${baseIndName.toLowerCase()} businesses and local employers in ${cityState}. Uncover unposted job opportunities and contact details.`)
    : sanitizeDuplicateWords(industry.shortDescription);

  const currentRoute = location
    ? `/jobs/${industry.slug}/${location.citySlug}`
    : `/industries/${industry.slug}`;

  useSeoMetadata({
    title: pageTitle,
    description: metaDescription,
    canonicalUrl: `https://getbiznearyou.com${currentRoute}`,
    schemaJson: {
      "@context": "https://schema.org",
      "@type": location ? "Place" : "WebPage",
      "name": pageTitle,
      "description": metaDescription,
      "url": `https://getbiznearyou.com${currentRoute}`
    }
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  const breadcrumbs = location
    ? [
        { label: "Industries", path: "/industries" },
        { label: industry.name, path: `/industries/${industry.slug}` },
        { label: cityState, path: currentRoute }
      ]
    : [
        { label: "Industries", path: "/industries" },
        { label: industry.name, path: currentRoute }
      ];

  const industrySampleCards: SampleBusinessCard[] = [
    {
      name: location ? `${location.cityName} ${industry.name} Associates` : `Premier ${industry.name} Group`,
      category: industry.name,
      location: location ? `${location.cityName}, ${location.stateAbbreviation}` : "National Directory",
      signalTier: "high",
      signalBadgeText: "High Signal",
      reason: `Direct owner portal & career intake active for ${industry.name.toLowerCase()} roles.`,
      freshness: "Verified 2 days ago",
      verifiability: "Verified Public Web Data"
    },
    {
      name: location ? `${location.cityName} Regional ${industry.name} Center` : `Apex ${industry.name} Partners`,
      category: industry.name,
      location: location ? `${location.cityName}, ${location.stateAbbreviation}` : "Regional Directory",
      signalTier: "possible",
      signalBadgeText: "Possible Signal",
      reason: `New office suite registration and active management contacts indexed.`,
      freshness: "Verified 4 days ago",
      verifiability: "Public Business Registry"
    },
    {
      name: location ? `${location.cityName} Custom ${industry.name} Services` : `Precision ${industry.name} Ops`,
      category: industry.name,
      location: location ? `${location.cityName}, ${location.stateAbbreviation}` : "Local Area",
      signalTier: "unconfirmed",
      signalBadgeText: "Unconfirmed Signal",
      reason: `25+ customer review updates indicating active operational expansion.`,
      freshness: "Verified 1 week ago",
      verifiability: "Review Stream Index"
    }
  ];

  return (
    <div className="w-full">
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <SeoBreadcrumbs items={breadcrumbs} onNavigate={onNavigate} />

        {/* Hero Section */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 shadow-sm">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-150 mb-3">
              <Building2 className="w-3.5 h-3.5" />
              <span>{location ? `Local Industry Report: ${cityState}` : "Industry Intelligence Hub"}</span>
            </span>

            <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              {location ? `${industry.name} Employers in ${cityState}` : `Discover ${industry.name} Near You`}
            </h1>

            <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6">
              {industry.fullDescription}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <a
                href={location ? `/jobs/${location.citySlug}/${getCategorySlugFromText(industry.sampleSearchCategory)}` : `/jobs/${getCategorySlugFromText(industry.sampleSearchCategory)}`}
                onClick={(e) => handleLinkClick(e, location ? `/jobs/${location.citySlug}/${getCategorySlugFromText(industry.sampleSearchCategory)}` : `/jobs/${getCategorySlugFromText(industry.sampleSearchCategory)}`)}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm transition flex items-center gap-2 cursor-pointer shadow-sm"
              >
                <Search className="w-4 h-4" />
                <span>{location ? `Search ${industry.name} in ${location.cityName}` : `Search Local ${industry.name}`}</span>
              </a>

              {location && (
                <a
                  href={`/jobs/${location.citySlug}`}
                  onClick={(e) => handleLinkClick(e, `/jobs/${location.citySlug}`)}
                  className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm transition flex items-center gap-2 cursor-pointer"
                >
                  <MapPin className="w-4 h-4" />
                  <span>All {location.cityName} Employers</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Search & Action Module */}
        <SeoSearchActionModule
          defaultLocation={location ? cityState : ""}
          defaultKeyword={industry.name}
          onNavigate={onNavigate}
        />

        {/* Example Results Preview */}
        <SeoExampleResultsPreview
          cards={industrySampleCards}
          locationName={location ? cityState : industry.name}
          onNavigate={onNavigate}
          onLogin={onLogin}
        />

        {/* Why Page Exists */}
        <SeoWhyPageExistsSection
          locationName={location ? cityState : industry.name}
          contextDescription={`BizNearYou indexes ${industry.name.toLowerCase()} businesses directly to help job seekers, recruiters, and sales teams connect with decision-makers.`}
        />

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Main Content (2 cols) */}
          <div className="lg:col-span-2 space-y-8">
            {/* Primary Employer Types */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-indigo-600" />
                <span>Primary Local Employer Types</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {industry.primaryEmployers.map((emp, i) => (
                  <div key={i} className="p-3.5 rounded-xl bg-slate-50 border border-slate-150 flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-xs font-semibold text-slate-800">{emp}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Entry Pathways */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                <span>Entry & Experience Pathways</span>
              </h2>
              <p className="text-xs text-slate-600 leading-relaxed mb-4">
                These common roles serve as entry points or specialized positions within {industry.name.toLowerCase()} businesses. Reaching out directly to owners or managers can uncover upcoming vacancies before ads are posted.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {industry.entryPathways.map((path, i) => (
                  <div key={i} className="p-3 rounded-xl bg-indigo-50/50 border border-indigo-100 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-600 flex-shrink-0" />
                    <span className="text-xs font-medium text-indigo-950">{path}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Outreach Tips */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
                <span>Proactive Outreach & Pitch Tips</span>
              </h2>
              <ul className="space-y-3">
                {industry.outreachTips.map((tip, i) => (
                  <li key={i} className="p-3.5 rounded-xl bg-slate-50 border border-slate-150 text-xs text-slate-700 leading-relaxed flex items-start gap-3">
                    <span className="font-bold text-indigo-600 flex-shrink-0">{i + 1}.</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar (1 col) */}
          <div className="space-y-6">
            {/* Hiring Signals Widget */}
            <div className="bg-indigo-50 dark:bg-indigo-950/60 text-slate-900 dark:text-white border border-indigo-200 dark:border-indigo-900/60 rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold mb-3 flex items-center gap-2 text-indigo-950 dark:text-white">
                <Activity className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span>Hiring Signals to Watch</span>
              </h3>
              <p className="text-xs text-indigo-800 dark:text-indigo-200 mb-4 leading-relaxed">
                When researching {industry.name.toLowerCase()} businesses, look for these key growth indicators:
              </p>
              <ul className="space-y-2.5 text-xs text-indigo-900 dark:text-indigo-100">
                {industry.hiringSignalIndicators.map((sig, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 dark:bg-emerald-400 mt-1.5 flex-shrink-0" />
                    <span>{sig}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Stats Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Industry Quick Stats</h3>
              <div className="space-y-3 text-xs">
                <div>
                  <span className="text-slate-500 block">Estimated Density:</span>
                  <span className="font-semibold text-slate-800">{industry.estimatedBusinessDensity}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Sample Search Category:</span>
                  <span className="font-semibold text-slate-800">{industry.sampleSearchCategory}</span>
                </div>
              </div>
            </div>

            {/* CTA Box */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 text-center shadow-sm">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-2">Ready to Search Local Employers?</h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-4">
                Use BizNearYou to map verified local businesses and contact managing partners directly.
              </p>
              <a
                href={location ? `/jobs/${location.citySlug}` : "/find-jobs"}
                onClick={(e) => handleLinkClick(e, location ? `/jobs/${location.citySlug}` : "/find-jobs")}
                className="w-full py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs transition inline-block cursor-pointer shadow-sm"
              >
                Launch Search Engine
              </a>
            </div>
          </div>
        </div>

        <SeoRelatedLinks currentRoute={currentRoute} onNavigate={onNavigate} />
      </main>
    </div>
  );
}
